import sort from '@bybeam/platform-lib/utilities/sort';
import { ApplicantType, ApplicantTypeRole, Partner, Program, User } from '@bybeam/platform-types';

export function getApplicantType(user: User, program?: Program): ApplicantType {
  if (!program) return user.applicantProfile.applicantType;

  const applicantTypes = getAvailableApplicantTypes(program);
  return applicantTypes.find(({ id }) => id === user.applicantProfile.applicantType.id);
}

export function getAvailableApplicantTypes(program: Program): ApplicantType[] {
  return program.applicantTypes.map(({ applicantType, nameOverride }) => ({
    ...applicantType,
    ...(nameOverride && { name: nameOverride }),
  }));
}

export function getUniqueApplicantTypes(programs: Program[]): ApplicantType[] {
  const allTypes = programs.flatMap(getAvailableApplicantTypes);
  const typesById: { [id: string]: ApplicantType[] } = {};
  for (const type of allTypes) {
    if (!typesById[type.id]) typesById[type.id] = [];
    typesById[type.id].push(type);
  }

  return Object.entries(typesById).map(([id, types]) => ({
    ...types[0],
    ...(new Set(types.map(({ name }) => name)).size > 1 && {
      // get the name without any override when there are different overrides per program.
      // a better experience might be to list all of the options and then filter by a
      // combination of program id + applicant type, but I think this will do for now.
      name: programs
        .flatMap((program) => program.applicantTypes)
        .find(({ applicantType }) => applicantType.id === id).applicantType.name,
    }),
  }));
}

export function getFirstPartyType(program: Program): ApplicantType {
  return getAvailableApplicantTypes(program).find(
    ({ role }) => role === ApplicantTypeRole.FirstParty,
  );
}

export function hasMultiParty(partner: Partner): boolean {
  return partner?.applicantTypes?.length > 1;
}

export function getDefaultApplicantTypeId(
  applicantTypes: ApplicantType[],
  role: ApplicantTypeRole = ApplicantTypeRole.FirstParty,
): string {
  if (!applicantTypes?.length) return undefined;
  switch (role) {
    case ApplicantTypeRole.ThirdParty: {
      const types = applicantTypes.filter(({ role }) => role === ApplicantTypeRole.ThirdParty);
      return types?.length === 1 ? types[0].id : undefined;
    }
    default:
      return applicantTypes.find(({ role }) => role === ApplicantTypeRole.FirstParty)?.id;
  }
}

export function getSortedApplicantTypes(
  applicantTypes: ApplicantType[],
  role?: ApplicantTypeRole,
): ApplicantType[] {
  const types = role ? applicantTypes.filter((type) => type.role === role) : [...applicantTypes];
  return sort(types, {
    accessor: (type) => type.name,
  });
}

export const isThirdPartyUser = (user: User): boolean => {
  return user?.applicantProfile?.applicantType?.role === ApplicantTypeRole.ThirdParty;
};
